import { APP_VERSION } from '../content/version.js';
import { apiBaseUrl } from '../platform/index.js';
import { getAuth } from '../store/auth.js';
import { getProfile } from '../store/profile.js';
import { supabaseBrowser } from '../store/supabaseClient.js';
import { getBugLogEvents, type BugLogEvent } from './bugLog.js';

/**
 * Beta diagnostic snapshot. Gathered into a flat-ish object so it can
 * be displayed in BetaDiagnosticModal and JSON-stringified to the
 * clipboard for bug reports. Every field is either a primitive or
 * null so JSON.stringify never throws.
 *
 * What's deliberately NOT in here:
 *   - JWT / access token (security risk if a tester shares the JSON)
 *   - Full email address (mask middle so we can still tell which
 *     account it is without leaking it to whoever the tester pastes
 *     the diagnostic to)
 *   - Server-side env vars or anything Supabase-keyed
 *
 * Pure module — safe to import without bringing in Phaser.
 */
export interface DiagnosticSnapshot {
  readonly timestamp: string;
  readonly app: {
    readonly version: string;
  };
  readonly auth: {
    readonly userIdShort: string | null;
    readonly emailMasked: string | null;
    readonly emailConfirmed: boolean;
    readonly sessionValid: boolean;
    readonly sessionExpiresAt: string | null;
  };
  readonly profile: {
    readonly username: string | null;
    readonly emailNotifications: boolean | null;
    readonly state: 'unloaded' | 'loading' | 'missing' | 'exists';
  };
  readonly network: {
    readonly api: {
      readonly status: number | null;
      readonly latencyMs: number | null;
      readonly error: string | null;
    };
    readonly realtime: {
      readonly status: RealtimeProbeStatus;
      readonly detail: string | null;
    };
  };
  readonly data: {
    readonly activeGames: number | null;
  };
  readonly client: {
    readonly userAgent: string;
  };
  /** Recent diagnostic events from the persistent bug-log buffer.
   *  Surfaced here so a tester's "Copy diagnostic" output includes
   *  the run-up to whichever bug they're reporting. Newest last. */
  readonly bugLog: readonly BugLogEvent[];
}

type RealtimeProbeStatus =
  | 'CONNECTED'
  | 'TIMED_OUT'
  | 'CHANNEL_ERROR'
  | 'CLOSED'
  | 'NO_SESSION';

const REALTIME_PROBE_TIMEOUT_MS = 2000;

/**
 * Mask an email so a tester can recognize it's theirs without leaking
 * the full address to anyone they paste the diagnostic to. First
 * letter + `***` + `@domain.tld`. `claytondreeves@gmail.com` →
 * `c***@gmail.com`.
 */
function maskEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  const first = email[0] ?? '*';
  return `${first}***${email.slice(at)}`;
}

/**
 * Hit GET /api/account with the user's Bearer token. Doubles as the
 * "active games count" check — the endpoint returns activeGameCount
 * + username, so we get two diagnostics for one round-trip.
 */
async function probeApi(token: string | null): Promise<{
  status: number | null;
  latencyMs: number | null;
  error: string | null;
  activeGames: number | null;
}> {
  if (!token) {
    return {
      status: null,
      latencyMs: null,
      error: 'no session',
      activeGames: null,
    };
  }
  const start = performance.now();
  try {
    const res = await fetch(`${apiBaseUrl()}/api/account`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const latencyMs = Math.round(performance.now() - start);
    let activeGames: number | null = null;
    if (res.ok) {
      try {
        const body = (await res.json()) as { activeGameCount?: number };
        if (typeof body.activeGameCount === 'number') {
          activeGames = body.activeGameCount;
        }
      } catch {
        // Body wasn't JSON — leave activeGames null. Status code is
        // still useful for the diagnostic.
      }
    }
    return { status: res.status, latencyMs, error: null, activeGames };
  } catch (err: unknown) {
    const latencyMs = Math.round(performance.now() - start);
    return {
      status: null,
      latencyMs,
      error: err instanceof Error ? err.message : String(err),
      activeGames: null,
    };
  }
}

/**
 * 2-second test-channel subscribe. Confirms the Supabase Realtime
 * WebSocket is actually flowing end-to-end (the cross-account MP
 * fix from v0.9.2 was a Realtime-evaluator bug, so this is exactly
 * the layer most likely to silently break in the future).
 *
 * Empty channel with no listeners — we only care whether the
 * subscribe handshake reaches SUBSCRIBED before the timeout fires.
 * If the user is signed-out the channel still establishes (anon WS
 * works), so we return CONNECTED in that case too; the absence of
 * a session is reported separately under auth.
 */
async function probeRealtime(): Promise<{
  status: RealtimeProbeStatus;
  detail: string | null;
}> {
  const supabase = supabaseBrowser();
  const channelName = `diagnostic-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  return new Promise((resolve) => {
    const channel = supabase.channel(channelName);
    let settled = false;

    const cleanup = (
      status: RealtimeProbeStatus,
      detail: string | null
    ): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      void supabase.removeChannel(channel);
      resolve({ status, detail });
    };

    const timer = setTimeout(() => {
      cleanup('TIMED_OUT', `no SUBSCRIBED after ${REALTIME_PROBE_TIMEOUT_MS}ms`);
    }, REALTIME_PROBE_TIMEOUT_MS);

    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        cleanup('CONNECTED', null);
      } else if (
        status === 'CHANNEL_ERROR' ||
        status === 'TIMED_OUT' ||
        status === 'CLOSED'
      ) {
        cleanup(
          status,
          err instanceof Error ? err.message : err ? String(err) : null
        );
      }
    });
  });
}

/**
 * Gather every diagnostic field. API + Realtime probes run in
 * parallel — they're independent, so there's no reason to serialize.
 * Total wall-time is bounded by the realtime probe's 2-second
 * timeout.
 */
export async function runDiagnostics(): Promise<DiagnosticSnapshot> {
  const auth = getAuth();
  const profile = getProfile();
  const token = auth.session?.access_token ?? null;

  const [api, realtime] = await Promise.all([probeApi(token), probeRealtime()]);

  const user = auth.user;
  const expiresAtSec = auth.session?.expires_at ?? null;
  const sessionValid =
    expiresAtSec != null && expiresAtSec * 1000 > Date.now();
  const sessionExpiresAt =
    expiresAtSec != null ? new Date(expiresAtSec * 1000).toISOString() : null;

  return {
    timestamp: new Date().toISOString(),
    app: { version: APP_VERSION },
    auth: {
      userIdShort: user?.id ? user.id.slice(0, 8) : null,
      emailMasked: maskEmail(user?.email ?? null),
      emailConfirmed: !!user?.email_confirmed_at,
      sessionValid,
      sessionExpiresAt,
    },
    profile: {
      username: profile.kind === 'exists' ? profile.profile.username : null,
      emailNotifications:
        profile.kind === 'exists'
          ? profile.profile.emailNotifications
          : null,
      state: profile.kind,
    },
    network: {
      api: {
        status: api.status,
        latencyMs: api.latencyMs,
        error: api.error,
      },
      realtime,
    },
    data: { activeGames: api.activeGames },
    client: {
      userAgent: navigator.userAgent.slice(0, 200),
    },
    bugLog: getBugLogEvents(),
  };
}
